import { z } from "zod";

// ---------------------------------------------------------------------------
// Enums (spec §4). Stored as CHECK constraints in Postgres, mirrored here as
// zod enums / TS union types so api + web share one source of truth.
// ---------------------------------------------------------------------------

export const UserRole = z.enum(["owner", "member", "superadmin"]);
export type UserRole = z.infer<typeof UserRole>;

export const PartyKind = z.enum(["person", "firm", "other"]);
export type PartyKind = z.infer<typeof PartyKind>;

export const AccountKind = z.enum([
  "bank",
  "credit_card",
  "cash",
  "wallet",
  "paylater",
]);
export type AccountKind = z.infer<typeof AccountKind>;

export const CategoryKind = z.enum(["expense", "income", "transfer"]);
export type CategoryKind = z.infer<typeof CategoryKind>;

export const DrCr = z.enum(["dr", "cr"]);
export type DrCr = z.infer<typeof DrCr>;

export const TransactionSource = z.enum(["manual", "import", "gmail"]);
export type TransactionSource = z.infer<typeof TransactionSource>;

export const AssetKind = z.enum([
  "property",
  "fd",
  "shares",
  "mf",
  "pf",
  "gold",
  "vehicle",
  "other",
]);
export type AssetKind = z.infer<typeof AssetKind>;

export const LoanKind = z.enum([
  "bank",
  "nbfc",
  "family",
  "employer",
  "card_loan",
]);
export type LoanKind = z.infer<typeof LoanKind>;

export const IncomeKind = z.enum([
  "salary",
  "rent",
  "interest",
  "dividend",
  "other",
]);
export type IncomeKind = z.infer<typeof IncomeKind>;

export const CapitalGainsAssetClass = z.enum([
  "equity",
  "mf",
  "property",
  "gold",
  "other",
]);
export type CapitalGainsAssetClass = z.infer<typeof CapitalGainsAssetClass>;

export const CapitalGainsTerm = z.enum(["short", "long"]);
export type CapitalGainsTerm = z.infer<typeof CapitalGainsTerm>;

export const DocumentType = z.enum([
  "salary_slip",
  "form16",
  "itr",
  "bank_stmt",
  "card_stmt",
  "deed",
  "loan_doc",
  "invoice",
  "insurance",
  "pan_aadhaar",
  "other",
]);
export type DocumentType = z.infer<typeof DocumentType>;

export const DocumentSource = z.enum(["upload", "bulk", "gmail"]);
export type DocumentSource = z.infer<typeof DocumentSource>;

export const OcrStatus = z.enum(["pending", "done", "skipped", "failed"]);
export type OcrStatus = z.infer<typeof OcrStatus>;

export const DocumentLinkTargetKind = z.enum([
  "party",
  "account",
  "asset",
  "loan",
  "transaction",
  "income",
]);
export type DocumentLinkTargetKind = z.infer<typeof DocumentLinkTargetKind>;

export const ReminderRule = z.enum([
  "monthly",
  "quarterly",
  "yearly",
  "once",
]);
export type ReminderRule = z.infer<typeof ReminderRule>;

export const ImportBatchStatus = z.enum([
  "preview",
  "committed",
  "discarded",
]);
export type ImportBatchStatus = z.infer<typeof ImportBatchStatus>;

export const CategorizeRuleMatchKind = z.enum(["contains", "regex"]);
export type CategorizeRuleMatchKind = z.infer<typeof CategorizeRuleMatchKind>;

// ---------------------------------------------------------------------------
// Auth input schemas
// ---------------------------------------------------------------------------

/** POST /api/auth/register-tenant (superadmin only) */
export const RegisterInput = z.object({
  tenantName: z.string().min(1),
  email: z.string().email(),
});
export type RegisterInput = z.infer<typeof RegisterInput>;

/** POST /api/auth/login */
export const LoginInput = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof LoginInput>;

/** POST /api/auth/accept-invite */
export const AcceptInviteInput = z.object({
  token: z.string().min(1),
  password: z.string().min(8),
});
export type AcceptInviteInput = z.infer<typeof AcceptInviteInput>;

/** POST /api/auth/forgot-password — always 200s regardless of what this resolves to (no user enumeration). */
export const ForgotPasswordInput = z.object({
  email: z.string().email(),
});
export type ForgotPasswordInput = z.infer<typeof ForgotPasswordInput>;

/** POST /api/auth/reset-password */
export const ResetPasswordInput = z.object({
  token: z.string().min(1),
  password: z.string().min(8),
});
export type ResetPasswordInput = z.infer<typeof ResetPasswordInput>;

/** POST /api/auth/admin-reset-password (superadmin only) */
export const AdminResetPasswordInput = z.object({
  email: z.string().email(),
});
export type AdminResetPasswordInput = z.infer<typeof AdminResetPasswordInput>;

// ---------------------------------------------------------------------------
// JWT payload shape shared by api routes/middleware
// ---------------------------------------------------------------------------

export interface AuthTokenPayload {
  userId: string;
  /** null for superadmin: superadmin accounts are not tenant-scoped. */
  tenantId: string | null;
  role: UserRole;
}

// ---------------------------------------------------------------------------
// Parties / accounts / categories / transactions CRUD input schemas (Task 2).
// Field names are camelCase (api/web contract); modules map them onto the
// snake_case DB columns, same convention as RegisterInput.tenantName above.
// ---------------------------------------------------------------------------

export const CreatePartyInput = z.object({
  name: z.string().min(1),
  kind: PartyKind,
  relation: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type CreatePartyInput = z.infer<typeof CreatePartyInput>;

export const UpdatePartyInput = CreatePartyInput.partial();
export type UpdatePartyInput = z.infer<typeof UpdatePartyInput>;

export const CreateAccountInput = z.object({
  kind: AccountKind,
  name: z.string().min(1),
  institution: z.string().nullable().optional(),
  numberMask: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
});
export type CreateAccountInput = z.infer<typeof CreateAccountInput>;

export const UpdateAccountInput = CreateAccountInput.partial();
export type UpdateAccountInput = z.infer<typeof UpdateAccountInput>;

export const CreateCategoryInput = z.object({
  name: z.string().min(1),
  kind: CategoryKind,
  parentId: z.string().uuid().nullable().optional(),
});
export type CreateCategoryInput = z.infer<typeof CreateCategoryInput>;

export const UpdateCategoryInput = CreateCategoryInput.partial();
export type UpdateCategoryInput = z.infer<typeof UpdateCategoryInput>;

/** date is a plain ISO `YYYY-MM-DD` string end-to-end (spec's global constraint). */
export const CreateTransactionInput = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date must be YYYY-MM-DD"),
  amount: z.number().positive(),
  drcr: DrCr,
  accountId: z.string().uuid(),
  partyId: z.string().uuid().nullable().optional(),
  categoryId: z.string().uuid().nullable().optional(),
  narration: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  source: TransactionSource.optional(),
});
export type CreateTransactionInput = z.infer<typeof CreateTransactionInput>;

export const UpdateTransactionInput = CreateTransactionInput.partial();
export type UpdateTransactionInput = z.infer<typeof UpdateTransactionInput>;

// ---------------------------------------------------------------------------
// Statement import (Task 3). Upload is multipart (validated in the module);
// commit takes optional per-row edits. `saveRule: true` upserts a 'contains'
// categorize_rule with the client-supplied `rulePattern`.
// ---------------------------------------------------------------------------

export const ImportRowEdit = z.object({
  rowId: z.string().uuid(),
  partyId: z.string().uuid().nullable().optional(),
  categoryId: z.string().uuid().nullable().optional(),
  include: z.boolean().optional(),
  saveRule: z.boolean().optional(),
  rulePattern: z.string().min(1).optional(),
});
export type ImportRowEdit = z.infer<typeof ImportRowEdit>;

export const CommitImportInput = z.object({
  edits: z.array(ImportRowEdit).optional(),
});
export type CommitImportInput = z.infer<typeof CommitImportInput>;

// ---------------------------------------------------------------------------
// Assets / loans / incomes / capital gains CRUD input schemas (Task 5).
// Same camelCase-in / snake_case-column convention as Task 2's schemas above.
// ---------------------------------------------------------------------------

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date must be YYYY-MM-DD");

export const CreateAssetInput = z.object({
  kind: AssetKind,
  name: z.string().min(1),
  acquiredOn: isoDate.nullable().optional(),
  cost: z.number().nullable().optional(),
  currentValue: z.number().nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type CreateAssetInput = z.infer<typeof CreateAssetInput>;

export const UpdateAssetInput = CreateAssetInput.partial();
export type UpdateAssetInput = z.infer<typeof UpdateAssetInput>;

/** POST /api/assets/:id/valuations — a single as-on-date snapshot value. */
export const CreateAssetValuationInput = z.object({
  asOn: isoDate,
  value: z.number(),
});
export type CreateAssetValuationInput = z.infer<typeof CreateAssetValuationInput>;

export const CreateLoanInput = z.object({
  lender: z.string().min(1),
  kind: LoanKind,
  loanNumber: z.string().nullable().optional(),
  principal: z.number().nullable().optional(),
  rate: z.number().nullable().optional(),
  emi: z.number().nullable().optional(),
  startDate: isoDate.nullable().optional(),
  tenureMonths: z.number().int().nullable().optional(),
  outstanding: z.number().nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type CreateLoanInput = z.infer<typeof CreateLoanInput>;

export const UpdateLoanInput = CreateLoanInput.partial();
export type UpdateLoanInput = z.infer<typeof UpdateLoanInput>;

export const CreateIncomeInput = z.object({
  kind: IncomeKind,
  periodMonth: isoDate,
  employerOrSource: z.string().nullable().optional(),
  gross: z.number().nullable().optional(),
  basic: z.number().nullable().optional(),
  net: z.number().nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type CreateIncomeInput = z.infer<typeof CreateIncomeInput>;

export const UpdateIncomeInput = CreateIncomeInput.partial();
export type UpdateIncomeInput = z.infer<typeof UpdateIncomeInput>;

/** PUT /api/incomes/salary/:month body — :month itself carries the period. */
export const UpsertSalaryInput = z.object({
  employer: z.string().nullable().optional(),
  gross: z.number().nullable().optional(),
  basic: z.number().nullable().optional(),
  net: z.number().nullable().optional(),
});
export type UpsertSalaryInput = z.infer<typeof UpsertSalaryInput>;

export const CreateCapitalGainInput = z.object({
  assetDesc: z.string().min(1),
  assetClass: CapitalGainsAssetClass,
  buyDate: isoDate,
  buyValue: z.number(),
  sellDate: isoDate,
  sellValue: z.number(),
  expenses: z.number().optional(),
  useIndexation: z.boolean().optional(),
  notes: z.string().nullable().optional(),
});
export type CreateCapitalGainInput = z.infer<typeof CreateCapitalGainInput>;

export const UpdateCapitalGainInput = CreateCapitalGainInput.partial();
export type UpdateCapitalGainInput = z.infer<typeof UpdateCapitalGainInput>;
// Document vault input schemas (Task 4). Multipart upload fields and the
// links body use the brief's literal snake_case keys (fy, doc_type,
// target_kind, target_id) rather than this file's usual camelCase — they
// mirror the DB columns directly, per the task brief.
// ---------------------------------------------------------------------------

/** POST /api/documents/:id/links body. */
export const CreateDocumentLinkInput = z.object({
  target_kind: DocumentLinkTargetKind,
  target_id: z.string().uuid(),
});
export type CreateDocumentLinkInput = z.infer<typeof CreateDocumentLinkInput>;

// ---------------------------------------------------------------------------
// Reminders CRUD + email notifications (Task 6). `nextDue` (the recurrence
// engine) lives in reminders.ts / the `@xynex-money/shared/reminders`
// subpath, same one-pure-module-per-subpath convention as capgains.ts —
// it's kept out of this schema file since it has nothing to do with input
// validation.
// ---------------------------------------------------------------------------

export const CreateReminderInput = z.object({
  title: z.string().min(1),
  amount: z.number().positive().nullable().optional(),
  dueDay: z.number().int().min(1).max(31),
  rrule: ReminderRule,
  /** First due date this reminder should fire for; POST /:id/paid advances it thereafter. */
  nextDue: isoDate,
  autopay: z.boolean().optional(),
  notifyEmail: z.boolean().optional(),
  active: z.boolean().optional(),
});
export type CreateReminderInput = z.infer<typeof CreateReminderInput>;

export const UpdateReminderInput = CreateReminderInput.partial();
export type UpdateReminderInput = z.infer<typeof UpdateReminderInput>;

/** POST /api/reminders/:id/paid body. */
export const MarkReminderPaidInput = z.object({
  createExpense: z.object({ accountId: z.string().uuid() }).optional(),
});
export type MarkReminderPaidInput = z.infer<typeof MarkReminderPaidInput>;

// ---------------------------------------------------------------------------
// Gmail auto-fetch (Task 8). `gmail_connections.rules` is a jsonb array of
// these; `account_id` is optional (only statement-type rules need one, to
// know which account a fetched bank/card statement's rows should import
// against). snake_case keys per the task brief's literal shape, same as the
// document-vault schemas above.
// ---------------------------------------------------------------------------

export const GmailRule = z.object({
  query: z.string().min(1),
  doc_type: DocumentType,
  account_id: z.string().uuid().optional(),
  /**
   * When set, attachments matched by this rule are treated as broker/registrar
   * CAPITAL-GAINS statements: the sync parses any .xlsx/.csv attachment via the
   * capital-gains importer and files the rows into `capital_gains` (in addition
   * to vaulting the document). The value is the fallback asset class for rows
   * whose class can't be inferred from the sheet. PDF attachments are only
   * vaulted (no CG parse yet).
   */
  capgains_default_class: CapitalGainsAssetClass.optional(),
});
export type GmailRule = z.infer<typeof GmailRule>;

/** POST /api/gmail/rules body: replaces the connection's rules wholesale. */
export const SetGmailRulesInput = z.array(GmailRule);
export type SetGmailRulesInput = z.infer<typeof SetGmailRulesInput>;
